import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { API_ORIGIN } from '../services/api';

interface VerificationCheck {
  id: string;
  name: string;
  status: 'pass' | 'fail' | 'warning' | 'info';
  message: string;
  details?: any;
}

interface VerificationResult {
  valid: boolean;
  documentId?: string;
  documentTitle?: string;
  checks: VerificationCheck[];
  summary: {
    overallStatus: 'valid' | 'invalid' | 'warning';
    totalChecks: number;
    passedChecks: number;
    failedChecks: number;
    warningChecks: number;
  };
}

interface AuthorityInfo {
  subject: string;
  sha256_fingerprint: string;
  not_before: string;
  not_after: string;
  pem_url: string;
}

function VerifyPage() {
  const navigate = useNavigate();
  const [authority, setAuthority] = useState<AuthorityInfo | null>(null);

  // Autorite de certification : empreinte publiee pour que chacun puisse controler le certificat importe
  useEffect(() => {
    fetch(`${API_ORIGIN}/ca/info`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => setAuthority(data))
      .catch(() => setAuthority(null));
  }, []);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VerificationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === 'application/pdf') {
        setFile(droppedFile);
        setResult(null);
        setError(null);
      } else {
        setError('Veuillez télécharger un fichier PDF');
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.type === 'application/pdf') {
        setFile(selectedFile);
        setResult(null);
        setError(null);
      } else {
        setError('Veuillez télécharger un fichier PDF');
      }
    }
  };

  const handleVerify = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_ORIGIN}/verify/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Erreur lors de la vérification');
      }

      setResult(data.verification);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'fail':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass':
        return 'bg-green-50 border-green-200';
      case 'fail':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Portail de Vérification</h1>
              <p className="mt-1 text-sm text-gray-600">
                Vérifiez l'authenticité et l'intégrité de vos documents signés
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Signature électronique PAdES</p>
              <p className="text-xs text-gray-500">Règlement eIDAS (UE) n° 910/2014</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Upload Section */}
        {!result && (
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <div className="text-center mb-6">
              <FileText className="w-16 h-16 text-blue-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Vérifier un document signé
              </h2>
              <p className="text-gray-600">
                Téléchargez un PDF signé pour vérifier sa signature et son intégrité
              </p>
            </div>

            {/* Drag and Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                dragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">
                Glissez-déposez votre PDF ici ou
              </p>
              <label className="inline-block">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleChange}
                  className="hidden"
                />
                <span className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer inline-block">
                  Parcourir les fichiers
                </span>
              </label>
              <p className="text-sm text-gray-500 mt-2">PDF uniquement, max 50MB</p>
            </div>

            {/* Selected File */}
            {file && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="w-8 h-8 text-blue-500 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setFile(null)}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            {/* Verify Button */}
            <div className="mt-6">
              <button
                onClick={handleVerify}
                disabled={!file || loading}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                  !file || loading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {loading ? 'Vérification en cours...' : 'Vérifier le document'}
              </button>
            </div>
          </div>
        )}

        {/* Results Section */}
        {result && (
          <div className="space-y-6">
            {/* Overall Status */}
            <div
              className={`rounded-lg p-6 border-2 ${
                result.summary.overallStatus === 'valid'
                  ? 'bg-green-50 border-green-500'
                  : result.summary.overallStatus === 'invalid'
                  ? 'bg-red-50 border-red-500'
                  : 'bg-yellow-50 border-yellow-500'
              }`}
            >
              <div className="flex items-center">
                {result.summary.overallStatus === 'valid' ? (
                  <CheckCircle className="w-12 h-12 text-green-500 mr-4" />
                ) : (
                  <AlertCircle
                    className={`w-12 h-12 mr-4 ${
                      result.summary.overallStatus === 'invalid'
                        ? 'text-red-500'
                        : 'text-yellow-500'
                    }`}
                  />
                )}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {result.summary.overallStatus === 'valid'
                      ? '✓ Document Valide'
                      : result.summary.overallStatus === 'invalid'
                      ? '✗ Document Invalide'
                      : '⚠ Avertissements détectés'}
                  </h2>
                  {result.documentTitle && (
                    <p className="text-gray-700 mt-1">Document: {result.documentTitle}</p>
                  )}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {result.summary.totalChecks}
                  </div>
                  <div className="text-sm text-gray-600">Total</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {result.summary.passedChecks}
                  </div>
                  <div className="text-sm text-gray-600">Réussis</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-600">
                    {result.summary.failedChecks}
                  </div>
                  <div className="text-sm text-gray-600">Échoués</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-600">
                    {result.summary.warningChecks}
                  </div>
                  <div className="text-sm text-gray-600">Avertissements</div>
                </div>
              </div>
            </div>

            {/* Checks Details */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Détails de la vérification
              </h3>
              <div className="space-y-3">
                {result.checks.map((check, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${getStatusColor(check.status)}`}
                  >
                    <div className="flex items-start">
                      <div className="mr-3 mt-0.5">{getStatusIcon(check.status)}</div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{check.name}</h4>
                        <p className="text-sm text-gray-700 mt-1">{check.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setResult(null);
                  setFile(null);
                }}
                className="flex-1 py-3 px-4 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700"
              >
                Vérifier un autre document
              </button>
              {result.documentId && (
                <button
                  onClick={() => navigate(`/verify/${result.documentId}`)}
                  className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                >
                  Voir le rapport complet
                </button>
              )}
            </div>
          </div>
        )}

        {/* Autorité de certification */}
        {authority && (
          <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Vérifier une signature dans Adobe Acrobat ou un autre validateur
            </h3>
            <p className="text-sm text-gray-700 mb-3">
              Les signatures sont émises par notre propre autorité de certification, qui ne figure pas dans la liste
              de confiance d'Adobe : Acrobat peut donc afficher « validité inconnue » alors que le document est intact.
              Pour la faire reconnaître, importez le certificat ci-dessous après avoir contrôlé son empreinte.
            </p>
            <dl className="text-sm space-y-1">
              <div className="flex gap-2">
                <dt className="font-medium text-gray-700">Autorité :</dt>
                <dd className="text-gray-900 break-all">{authority.subject}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="font-medium text-gray-700 shrink-0">Empreinte SHA-256 :</dt>
                <dd className="font-mono text-xs text-gray-900 break-all">{authority.sha256_fingerprint}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="font-medium text-gray-700">Valide du :</dt>
                <dd className="text-gray-900">
                  {new Date(authority.not_before).toLocaleDateString('fr-FR')} au{' '}
                  {new Date(authority.not_after).toLocaleDateString('fr-FR')}
                </dd>
              </div>
            </dl>
            <a
              href={authority.pem_url}
              className="inline-block mt-3 text-sm font-medium text-blue-700 hover:underline"
            >
              Télécharger le certificat de l'autorité (ca.pem)
            </a>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            À propos de la vérification
          </h3>
          <div className="text-sm text-blue-800 space-y-2">
            <p>
              Ce portail vérifie l'authenticité et l'intégrité des documents signés
              électroniquement avec notre service.
            </p>
            <p className="font-medium mt-4">Les vérifications incluent :</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Signature cryptographique de chaque signataire (intégrité, certificat, révocation)</li>
              <li>Horodatage RFC 3161 de chaque signature et du document</li>
              <li>Aucune modification du document après la signature</li>
              <li>Intégrité et ancrage de la chaîne d'audit</li>
              <li>Ancrage Bitcoin (OpenTimestamps), une fois inscrit</li>
            </ul>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 border-t border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-gray-600">
          <p>
            Vérifié par <span className="font-semibold">Juriseal</span> —
            signature électronique PAdES, règlement eIDAS
          </p>
        </div>
      </footer>
    </div>
  );
}

export default VerifyPage;
